/**
 * Authentication state for the SPA.
 *
 * Security model: the short-lived access token is held in memory only (never
 * persisted), and the long-lived refresh token lives in an HttpOnly cookie the
 * JS never sees. On load we attempt a silent `/auth/refresh`; if the cookie is
 * valid we get a fresh access token and hydrate the user. The API client calls
 * `registerAuthLostHandler` so an unrecoverable 401 anywhere drops us to anon.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, registerAuthLostHandler, restoreSession, setAccessToken } from '@/lib/api';
import type { Role, UserResponse } from '@/lib/types';

type Status = 'loading' | 'authenticated' | 'anonymous';

interface AuthApi {
  status: Status;
  user: UserResponse | null;
  hasRole: (...roles: Role[]) => boolean;
  isAdmin: boolean;
  isStaff: boolean; // ADMIN or DEV
  login: (usernameOrEmail: string, password: string) => Promise<void>;
  register: (input: { email: string; username: string; password: string; displayName?: string }) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthApi | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<UserResponse | null>(null);

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setStatus('anonymous');
    // Drop any cached data from the previous identity (e.g. a guest's chat
    // conversations) so we never reuse it under a different owner.
    qc.clear();
  }, [qc]);

  const hydrate = useCallback(async () => {
    const me = await api.auth.me();
    setUser(me);
    setStatus('authenticated');
  }, []);

  // Silent session restore on first load.
  useEffect(() => {
    registerAuthLostHandler(clear);
    let cancelled = false;
    (async () => {
      // Restore through the shared single-flight refresh so StrictMode's
      // double-mount (or several tabs) can't fire two token rotations and trip
      // the backend's theft-detection into a full logout.
      const ok = await restoreSession();
      if (cancelled) return;
      if (!ok) {
        clear();
        return;
      }
      // Discard any cache populated while we were still 'loading' (e.g. a
      // guest's empty chat conversations queried on a public page) so the
      // restored identity never reuses another owner's data — mirrors login().
      qc.clear();
      try {
        await hydrate();
      } catch {
        if (!cancelled) clear();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clear, hydrate, qc]);

  const login = useCallback(async (usernameOrEmail: string, password: string) => {
    const token = await api.auth.login({ usernameOrEmail, password });
    setAccessToken(token.accessToken);
    // Keep what the visitor did as a guest: their conversations move into the
    // account. Best-effort — a failure must never block signing in.
    try {
      await api.chat.adoptGuest();
    } catch {
      // the guest threads stay on the guest cookie; nothing else depends on this
    }
    // Discard any guest-session cache so we don't reuse a prior owner's data
    // under the new identity (adopted threads are re-fetched as the user's).
    qc.clear();
    await hydrate();
  }, [hydrate, qc]);

  const register = useCallback(async (input: { email: string; username: string; password: string; displayName?: string }) => {
    await api.auth.register(input);
    await login(input.username, input.password);
  }, [login]);

  const logout = useCallback(async () => {
    try {
      await api.auth.logout();
    } catch {
      // The server-side revoke failing (network, expired session) must not
      // block the local sign-out — the cookie is HttpOnly and short-lived.
    } finally {
      clear();
    }
  }, [clear]);

  const refreshUser = useCallback(async () => {
    setUser(await api.auth.me());
  }, []);

  const value = useMemo<AuthApi>(() => {
    const roles = user?.roles ?? [];
    return {
      status,
      user,
      hasRole: (...r: Role[]) => r.some((role) => roles.includes(role)),
      isAdmin: roles.includes('ADMIN'),
      isStaff: roles.includes('ADMIN') || roles.includes('DEV'),
      login,
      register,
      logout,
      refreshUser,
    };
  }, [status, user, login, register, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
